import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { unparse } from 'papaparse';
import { useState, useEffect, useMemo } from 'react';
import { typedFetch } from '../../typedFetch';
import { PrettyDate } from '../PrettyDate';
import { STATUSES } from '../STATUSES';
import './Tickets.scss';
const params = new URLSearchParams(window.location.search);
// All sorting will be done through one sorting type, and whether that sort is reversed or not, in state (primarily for visuals)
// and through a state array that gets sorted when one of the above options is changed.
// To increase sorting ability without increasing state complication, this takes advantage of the stable sort guarantee
// so that one can, for example, sort by newest submitted, and then sort by requester,
// so that each requester is listed in order, but each of their tickets are sorted by submission time.
// Future loads of the page will only sort by the two non-array values rather than preserving everything, which is OK
const sortingFunctions = {
    oldestFirst: (ticket) => String(ticket.submitTimestamp),
    requesterAZ: (ticket) => ticket.requesterName,
    requestedCompletionLatest: (ticket) => String(ticket.requestedCompletionTimestamp),
    actualCompletionLatest: (ticket) => String(ticket.actualCompletionTimestamp),
};
export const Tickets = ({ appendToErrorLog, loggedInUser, }) => {
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [allWorkerNames, setAllWorkerNames] = useState([]);
    const [showWorkerNames, setShowWorkerNames] = useState([]);
    const [showRelativeDates, setShowRelativeDates] = useState(localStorage.getItem('showRelativeDates') === 'yes');
    useEffect(() => {
        localStorage.setItem('showRelativeDates', showRelativeDates ? 'yes' : '');
    }, [showRelativeDates]);
    const [showDepartments, setShowDepartments] = useState([]);
    const [showDescription, setShowDescription] = useState(localStorage.getItem('showDescription') === 'yes');
    useEffect(() => {
        localStorage.setItem('showDescription', showDescription ? 'yes' : '');
    }, [showDescription]);
    useEffect(() => {
        if (!loggedInUser) {
            appendToErrorLog('Only logged-in users may see the list of tickets');
            setLoading(false);
            return;
        }
        typedFetch('get-all-tickets')
            .then((result) => {
            if (result.success) {
                setTickets(result.tickets);
                setDepartments(result.departments);
                setAllWorkerNames(['', ...result.allWorkerNames]);
                setShowWorkerNames(['', ...result.allWorkerNames]);
                const paramDepartments = params.get('departments')?.split('|');
                setShowDepartments(paramDepartments?.length
                    ? result.departments.filter(dept => paramDepartments.includes(dept.toLowerCase()))
                    : result.departments);
            }
            else {
                appendToErrorLog(`Failed to retrieve list of tickets: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while retrieving list of tickets: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    }, [loggedInUser, appendToErrorLog]);
    const exportTickets = () => {
        const toTime = (timestamp) => {
            const date = new Date(timestamp);
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
        };
        const csv = unparse(tickets.map(({ submitTimestamp, requestedCompletionTimestamp, actualCompletionTimestamp, ...rest }) => ({
            submitTimestamp: toTime(submitTimestamp),
            requestedCompletionTimestamp: toTime(requestedCompletionTimestamp),
            actualCompletionTimestamp: actualCompletionTimestamp ? toTime(actualCompletionTimestamp) : '',
            ...rest,
        })));
        const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.download = `Tickets as of ${(new Date().toLocaleDateString()).replaceAll('/', '-')}`;
        a.href = window.URL.createObjectURL(csvFile);
        a.click();
    };
    const [showDisplayOptions, setShowDisplayOptions] = useState(Boolean(localStorage.getItem('showDisplayOptions')));
    useEffect(() => {
        localStorage.setItem('showDisplayOptions', showDisplayOptions ? 'show' : '');
    }, [showDisplayOptions]);
    const [showStatus, setShowStatus] = useState(() => {
        const statusParam = params.get('status');
        if (statusParam) {
            const statuses = statusParam
                .split('|')
                .map(str => str[0].toUpperCase() + str.slice(1).replaceAll('-', ' '))
                .filter(str => STATUSES.includes(str));
            if (statuses.length) {
                return Object.fromEntries(statuses.map(prop => [prop, true]));
            }
        }
        const storageStatus = localStorage.getItem('showStatus');
        if (storageStatus) {
            try {
                const status = JSON.parse(storageStatus);
                if (status && typeof status === 'object') {
                    return status;
                }
            }
            catch {
                // Malformatted JSON. Can't do anything but skip.
                localStorage.removeItem('showStatus');
            }
        }
        return Object.fromEntries(STATUSES.map(prop => [prop, true]));
    });
    useEffect(() => {
        localStorage.setItem('showStatus', JSON.stringify(showStatus));
    }, [showStatus]);
    // The below assumes accurate storage and so is type-imprecise,  but it doesn't matter
    const [sortType, setSortType] = useState(() => {
        const sortingStore = localStorage.getItem('sorting');
        if (sortingStore && Object.hasOwn(sortingFunctions, sortingStore)) {
            return sortingStore;
        }
        return 'oldestFirst';
    });
    useEffect(() => {
        localStorage.setItem('sorting', sortType);
    }, [sortType]);
    const [reverseSorting, setReverseSorting] = useState(localStorage.getItem('reverseSorting') === 'yes');
    useEffect(() => {
        localStorage.setItem('reverseSorting', reverseSorting ? 'yes' : '');
    }, [reverseSorting]);
    useEffect(() => {
        // Sort and set the state for all tickets, rather than setting a sorted state of only those being displayed,
        // so that later sorting sorts the entire collection (see explanation above)
        const newTickets = tickets
            .toSorted((a, b) => sortingFunctions[sortType](reverseSorting ? b : a).localeCompare(sortingFunctions[sortType](reverseSorting ? a : b), 'en', { numeric: true }));
        setTickets(newTickets);
    }, [
        sortType,
        reverseSorting,
        tickets.length,
    ]);
    const ticketsToDisplay = useMemo(() => tickets.filter(ticket => (showDepartments.includes(ticket.department) && showStatus[ticket.status] && showWorkerNames.includes(ticket.workerName))), [
        tickets,
        showStatus,
        showDepartments,
        showWorkerNames,
    ]);
    if (loading) {
        return 'Processing...';
    }
    if (!loggedInUser) {
        return 'No tickets to display';
    }
    return (_jsxs("main", { className: "tickets", children: [_jsxs("div", { children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showStatus.Submitted && showStatus.Accepted && showStatus['In progress'] && showStatus['Work not accepted'] && !showStatus.Verified && !showStatus['On hold'] ? !showStatus.Wontfix : undefined, onChange: () => {
                                    const onlyNeedsAttention = [
                                        'Submitted',
                                        'Accepted',
                                        'In progress',
                                        'Work not accepted',
                                    ];
                                    if (onlyNeedsAttention.every(prop => showStatus[prop] && Object.values(showStatus).reduce((a, val) => a + Number(val), 0) === 4)) {
                                        setShowStatus(Object.fromEntries((STATUSES).map(prop => [prop, true])));
                                    }
                                    else {
                                        setShowStatus({ 'Submitted': true, 'Accepted': true, 'In progress': true, 'Work not accepted': true });
                                    }
                                } }), "Show only tickets that need your attention"] }), _jsxs("label", { children: [_jsx("button", { onClick: () => {
                                    setShowDescription(!showDescription);
                                }, children: tickets.length === 0
                                    ? '?'
                                    : showDescription
                                        ? '▼'
                                        : '▶' }), "Descriptions"] }), _jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showRelativeDates, onChange: (e) => { setShowRelativeDates(e.currentTarget.checked); } }), "Relative dates"] }), _jsx("a", { href: "/create-ticket", children: _jsx("button", { children: "Submit ticket" }) }), _jsx("button", { onClick: exportTickets, title: "Exports all tickets, not just those displayed here", children: "Export all to CSV" })] }), _jsx("button", { onClick: () => { setShowDisplayOptions(!showDisplayOptions); }, children: String.fromCodePoint(0x26_99, 0xFE_0F) }), _jsxs("div", { className: "table", children: [_jsxs("div", { className: `header${showDisplayOptions ? '' : ' hide-options'}`, children: [_jsxs("div", { children: [_jsxs("div", { className: "option statuses", children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: Object.values(showStatus).some(Boolean), className: (() => {
                                                            const trueCount = Object.values(showStatus).reduce((a, val) => a + Number(val), 0);
                                                            return trueCount === 0 || trueCount === STATUSES.length
                                                                ? ''
                                                                : 'partial';
                                                        })(), onChange: () => {
                                                            setShowStatus(Object.values(showStatus).some(Boolean)
                                                                ? {}
                                                                : Object.fromEntries((STATUSES).map(prop => [prop, true])));
                                                        } }), _jsx("div", { children: "All" })] }), STATUSES.map(status => (_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showStatus[status] === true, onChange: (e) => { setShowStatus({ ...showStatus, [status]: e.currentTarget.checked }); } }), _jsx("div", { children: status })] }, status)))] }), _jsx("div", { children: "Status" })] }), _jsxs("div", { className: "columns", children: [_jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                        if (sortType === 'oldestFirst') {
                                                            setReverseSorting(!reverseSorting);
                                                        }
                                                        else {
                                                            setSortType('oldestFirst');
                                                            setReverseSorting(false);
                                                        }
                                                    }, children: sortType === 'oldestFirst' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Link" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                        if (sortType === 'requesterAZ') {
                                                            setReverseSorting(!reverseSorting);
                                                        }
                                                        else {
                                                            setSortType('requesterAZ');
                                                            setReverseSorting(false);
                                                        }
                                                    }, children: sortType === 'requesterAZ' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Requester" })] }), _jsxs("div", { children: [_jsx("div", { className: "option checkboxes", children: departments.map(department => (_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showDepartments.includes(department), onChange: (e) => {
                                                                setShowDepartments(e.currentTarget.checked
                                                                    ? [...showDepartments, department]
                                                                    : showDepartments.filter(dept => dept !== department));
                                                            } }), _jsx("div", { children: department })] }, department))) }), _jsx("div", { children: "Department" })] }), _jsxs("div", { children: [_jsx("div", { className: "option checkboxes", children: allWorkerNames.map(workerName => (_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showWorkerNames.includes(workerName), onChange: (e) => {
                                                                setShowWorkerNames(e.currentTarget.checked
                                                                    ? [...showWorkerNames, workerName]
                                                                    : showWorkerNames.filter(name => name !== workerName));
                                                            } }), _jsx("div", { children: workerName === '' ? '?' : workerName })] }, workerName))) }), _jsx("div", { children: "Worker" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                        if (sortType === 'oldestFirst') {
                                                            setReverseSorting(!reverseSorting);
                                                        }
                                                        else {
                                                            setSortType('oldestFirst');
                                                            setReverseSorting(false);
                                                        }
                                                    }, children: sortType === 'oldestFirst' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Submitted" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                        if (sortType === 'requestedCompletionLatest') {
                                                            setReverseSorting(!reverseSorting);
                                                        }
                                                        else {
                                                            setSortType('requestedCompletionLatest');
                                                            setReverseSorting(false);
                                                        }
                                                    }, children: sortType === 'requestedCompletionLatest' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Req. completion" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                        if (sortType === 'actualCompletionLatest') {
                                                            setReverseSorting(!reverseSorting);
                                                        }
                                                        else {
                                                            setSortType('actualCompletionLatest');
                                                            setReverseSorting(false);
                                                        }
                                                    }, children: sortType === 'actualCompletionLatest' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Actual completion" })] })] })] }), ticketsToDisplay.map((ticket) => {
                        const className = `${ticket.status.toLowerCase().replaceAll(' ', '-')} ticket`;
                        return (_jsxs("div", { className: className, children: [_jsx("div", { className: "status", children: ticket.status }), _jsxs("div", { children: [_jsxs("div", { className: "columns", children: [_jsxs("div", { children: [_jsxs("a", { href: `tickets/${ticket.id}`, children: [`#${ticket.id}`, ":"] }), _jsx("div", { children: ticket.title })] }), _jsx("div", { title: `${ticket.requesterEmail}, ${ticket.requesterName}`, children: ticket.requesterEmail }), _jsx("div", { children: ticket.department }), _jsx("div", { children: ticket.workerName || '?' }), _jsx("div", { children: _jsx(PrettyDate, { timestamp: ticket.submitTimestamp, showRelativeDates }) }), _jsx("div", { className: (() => {
                                                        const stillWorkToDo = [
                                                            'Submitted',
                                                            'Accepted',
                                                            'In progress',
                                                            'On hold',
                                                            'Work not accepted',
                                                        ].includes(ticket.status);
                                                        return stillWorkToDo && ticket.requestedCompletionTimestamp < Date.now() ? 'request-late' : '';
                                                    })(), children: _jsx(PrettyDate, { timestamp: ticket.requestedCompletionTimestamp, includeTime: false, showRelativeDates }) }), _jsx("div", { className: (() => {
                                                        const finished = ['Completed', 'Verified'].includes(ticket.status);
                                                        return finished && ticket.actualCompletionTimestamp > ticket.requestedCompletionTimestamp ? 'actual-late' : '';
                                                    })(), children: ticket.actualCompletionTimestamp
                                                        ? _jsx(PrettyDate, { timestamp: ticket.actualCompletionTimestamp, showRelativeDates })
                                                        : null })] }), _jsx("div", { className: `description${showDescription ? ' show' : ''}`, children: ticket.description })] })] }, ticket.id));
                    })] })] }));
};
